import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Stack,
  Avatar,
  Paper,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Person,
  Business,
  People,
  CalendarToday,
  Label,
  Description,
} from '@mui/icons-material';
import { Client } from '@/types';
import { format } from 'date-fns';

interface ClientDetailProps {
  client: Client;
}

const ClientDetail: React.FC<ClientDetailProps> = ({ client }) => {
  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      lead: 'default',
      prospect: 'primary',
      active: 'success',
      under_contract: 'warning',
      closed: 'success',
      inactive: 'error',
    };
    return colorMap[status] || 'default';
  };

  const budgetRange = client.preferences.priceRange
    ? `${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: client.preferences.priceRange.currency || 'USD',
        minimumFractionDigits: 0,
      }).format(client.preferences.priceRange.min)} - ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: client.preferences.priceRange.currency || 'USD',
        minimumFractionDigits: 0,
      }).format(client.preferences.priceRange.max)}`
    : 'Not specified';

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Main Info */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Profile Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar 
                    sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main', fontSize: '1.5rem' }}
                  >
                    {client.profile.firstName[0]}{client.profile.lastName[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {client.profile.firstName} {client.profile.lastName}
                    </Typography>
                    <Chip 
                      label={client.status.replace('_', ' ')} 
                      color={getStatusColor(client.status)} 
                      size="small" 
                      sx={{ mt: 0.5, textTransform: 'capitalize' }} 
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Email color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                          <Typography variant="body2">{client.profile.email}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Phone color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
                          <Typography variant="body2">{client.profile.phone || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationOn color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Address</Typography>
                          <Typography variant="body2">
                            {client.profile.address 
                              ? `${client.profile.address.city}, ${client.profile.address.state}`
                              : 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Business color="action" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Occupation</Typography>
                          <Typography variant="body2">{client.profile.occupation || 'Not provided'}</Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Preferences Card */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  Preferences
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">Property Types</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {client.preferences.propertyTypes.map((type) => (
                        <Chip key={type} label={type} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">Budget Range</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{budgetRange}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">Preferred Locations</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {client.preferences.location.cities.map((city) => (
                        <Chip key={city} label={city} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">Bedrooms / Bathrooms</Typography>
                    <Typography variant="body2">
                      {client.preferences.features.bedrooms.min}-{client.preferences.features.bedrooms.max} Beds • {client.preferences.features.bathrooms.min}-{client.preferences.features.bathrooms.max} Baths
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Notes Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Description color="action" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Notes
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                  {client.notes || 'No notes available for this client.'}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Sidebar Info */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  System Info
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Source</Typography>
                    <Chip label={client.source} size="small" variant="outlined" sx={{ mt: 0.5, textTransform: 'capitalize' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Agent Assigned</Typography>
                    <Typography variant="body2">ID: {client.agentId}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Created On</Typography>
                    <Typography variant="body2">{format((client.createdAt as any).toDate(), 'PPP')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Last Updated</Typography>
                    <Typography variant="body2">{format((client.updatedAt as any).toDate(), 'PPP')}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Label color="action" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Tags
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {client.tags && client.tags.length > 0 ? (
                    client.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No tags added</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientDetail;
